import { cn } from '@/lib/utils';
import { toDateKey, weekdayShort } from '@/components/admin/booking/bookingCalendarUtils';
import type { CalendarView } from '@/components/admin/booking/useBookingCalendar';

interface BookingCalendarSkeletonProps {
  view?: CalendarView;
  days?: Date[];
  hours?: string[];
  now?: Date;
  fieldCount?: number;
  className?: string;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

const DEFAULT_HOURS = [
  '07:00',
  '08:00',
  '09:00',
  '10:00',
  '11:00',
  '12:00',
  '13:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '20:00',
  '21:00',
  '22:00',
];

export default function BookingCalendarSkeleton({
  view = 'week',
  days,
  hours,
  now = new Date(),
  fieldCount = 4,
  className,
}: BookingCalendarSkeletonProps) {
  const displayHours = hours && hours.length > 0 ? hours : DEFAULT_HOURS;
  const todayKey = toDateKey(now);

  // Day View Skeleton
  if (view === 'day') {
    const colCount = Math.max(1, Math.min(fieldCount, 6));

    return (
      <div className={cn('space-y-3', className)}>
        <div className="overflow-x-auto rounded-2xl border border-[#E0E6ED] bg-white shadow-2xs">
          <div className="min-w-[760px]">
            {/* Header: Field columns */}
            <div className="flex border-b border-[#E0E6ED] bg-white">
              <div className="w-16 sm:w-20 shrink-0 border-r border-[#E0E6ED] p-3 bg-white" />
              {Array.from({ length: colCount }).map((_, i) => (
                <div key={i} className="flex-1 border-r border-[#E0E6ED] p-3 last:border-r-0">
                  <div className="h-4 w-28 rounded-md bg-gray-200 animate-pulse" />
                  <div className="h-3 w-16 rounded-md bg-gray-100 mt-1.5 animate-pulse" />
                </div>
              ))}
            </div>

            {/* Rows: Hours */}
            {displayHours.map((hour, rIdx) => (
              <div key={hour} className="flex border-b border-[#E0E6ED] last:border-b-0 min-h-[85px]">
                <div className="w-16 sm:w-20 shrink-0 border-r border-[#E0E6ED] p-2 align-top text-center select-none bg-white">
                  <span className="text-xs font-semibold text-gray-300 pt-1 block">
                    {hour.replace(':', '.')}
                  </span>
                </div>
                {Array.from({ length: colCount }).map((_, cIdx) => {
                  const showCard = (rIdx + cIdx) % 3 === 0;
                  return (
                    <div key={cIdx} className="flex-1 border-r border-[#E0E6ED] p-2 last:border-r-0">
                      {showCard && (
                        <div className="w-full h-14 rounded-xl p-2 border border-gray-100 bg-gray-50/80 space-y-1.5 animate-pulse">
                          <div className="h-3 w-16 rounded-sm bg-gray-200" />
                          <div className="h-3.5 w-24 rounded-sm bg-gray-200/80" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Legend Placeholder */}
        <div className="flex flex-wrap items-center gap-4 pt-2 text-xs text-gray-300">
          <div className="h-3 w-16 bg-gray-100 rounded-full animate-pulse" />
          <div className="h-3 w-20 bg-gray-100 rounded-full animate-pulse" />
          <div className="h-3 w-16 bg-gray-100 rounded-full animate-pulse" />
        </div>
      </div>
    );
  }

  // Week View Skeleton (matching the exact table structure of BookingWeekGrid)
  return (
    <div className={cn('space-y-3', className)}>
      <div className="overflow-x-auto rounded-2xl border border-[#E0E6ED] bg-white shadow-2xs">
        <table className="w-full min-w-[980px] border-collapse table-fixed text-left">
          {/* Header Row: 7 Days of the week */}
          <thead>
            <tr className="border-b border-[#E0E6ED] bg-white">
              {/* Corner Hours column */}
              <th className="w-16 sm:w-20 border-r border-[#E0E6ED] p-2 bg-white" />

              {/* 7 Days Columns */}
              {days && days.length === 7
                ? days.map((day) => {
                    const dateKey = toDateKey(day);
                    const isToday = dateKey === todayKey;

                    return (
                      <th
                        key={dateKey}
                        className={cn(
                          'p-2 text-center border-r border-[#E0E6ED] last:border-r-0 select-none',
                          isToday && 'bg-[#FFF5EE]/30'
                        )}
                      >
                        {isToday ? (
                          <div className="bg-[#FFF5EE] border border-orange-200/60 rounded-xl py-1.5 px-2">
                            <span className="block text-[11px] font-semibold text-[#FC480D] leading-tight">
                              Hari ini
                            </span>
                            <span className="block text-xs sm:text-sm font-semibold text-[#FC480D] leading-tight mt-0.5">
                              {day.getDate()} {MONTH_SHORT[day.getMonth()]}
                            </span>
                          </div>
                        ) : (
                          <div className="py-1">
                            <span className="block text-[11px] font-medium text-gray-400 leading-tight">
                              {weekdayShort(day)}
                            </span>
                            <span className="block text-xs sm:text-sm font-semibold text-[#0E1726] leading-tight mt-0.5">
                              {day.getDate()} {MONTH_SHORT[day.getMonth()]}
                            </span>
                          </div>
                        )}
                      </th>
                    );
                  })
                : ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'].map((dName) => (
                    <th key={dName} className="p-2 text-center border-r border-[#E0E6ED] last:border-r-0">
                      <div className="py-1">
                        <span className="block text-[11px] font-medium text-gray-400 leading-tight">
                          {dName}
                        </span>
                        <div className="h-4 w-12 mx-auto rounded-md mt-1 bg-gray-200 animate-pulse" />
                      </div>
                    </th>
                  ))}
            </tr>
          </thead>

          {/* Rows: Hours */}
          <tbody>
            {displayHours.map((hour, rIdx) => (
              <tr key={hour} className="border-b border-[#E0E6ED] last:border-b-0">
                {/* Side Hour Cell */}
                <td className="w-16 sm:w-20 border-r border-[#E0E6ED] p-2 align-top text-center select-none bg-white">
                  <span className="text-xs font-semibold text-gray-300 pt-1 block">
                    {hour.replace(':', '.')}
                  </span>
                </td>

                {/* 7 Day cells */}
                {Array.from({ length: 7 }).map((_, cIdx) => {
                  const day = days && days[cIdx];
                  const isToday = day ? toDateKey(day) === todayKey : cIdx === 3;
                  const showCard1 = (rIdx + cIdx) % 3 === 0;
                  const showCard2 = (rIdx * 2 + cIdx) % 5 === 0;

                  return (
                    <td
                      key={cIdx}
                      className={cn(
                        'border-r border-[#E0E6ED] last:border-r-0 p-1.5 align-top min-h-[90px]',
                        isToday && 'bg-[#FFF5EE]/15'
                      )}
                    >
                      <div className="space-y-1.5 min-h-[72px]">
                        {showCard1 && (
                          <div className="w-full rounded-xl p-2 border border-gray-100 bg-gray-50/90 space-y-1 animate-pulse">
                            <div className="h-2.5 w-10 rounded-sm bg-gray-200" />
                            <div className="h-3 w-20 rounded-sm bg-gray-200/80" />
                          </div>
                        )}
                        {showCard2 && (
                          <div className="w-full rounded-xl p-2 border border-gray-100 bg-gray-50/90 space-y-1 animate-pulse">
                            <div className="h-2.5 w-12 rounded-sm bg-gray-200" />
                            <div className="h-3 w-24 rounded-sm bg-gray-200/80" />
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend Placeholder */}
      <div className="flex flex-wrap items-center gap-4 pt-2 text-xs text-gray-300">
        <div className="h-3 w-16 bg-gray-100 rounded-full animate-pulse" />
        <div className="h-3 w-20 bg-gray-100 rounded-full animate-pulse" />
        <div className="h-3 w-16 bg-gray-100 rounded-full animate-pulse" />
        <div className="h-3 w-16 bg-gray-100 rounded-full animate-pulse" />
      </div>
    </div>
  );
}
